import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { ProgressBarComponent } from '../progress-bar/progress-bar.component';
import { QuestionCardComponent } from '../question-card/question-card.component';
import { Survey, SurveyQuestion, SurveyResponse } from '@app/schemas/surveys';
import { Store } from '@ngrx/store';
import { MatDialog } from '@angular/material/dialog';
import { ScoresComponent } from '../scores/scores.component';
import { updateSurveyResponse, setRiasecScore } from '@app/state/actions/surveys.action';
import { NgxSpinnerService } from 'ngx-spinner';
import { SurveysService } from '@app/services/api/surveys/surveys.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { WarningSnackBarComponent } from '@app/shared/snack-bars/warning/warning.component';
import { WarningDefaultOptions } from '@app/shared/snack-bars/warning/warning.utils';
import { CareerSurveyStore } from '@app/services/signals/career-survey-service';
import { SurveyStorageService } from '@app/services/local-storage/survey-storage.service';
import { DebugModeService } from '@app/services/debug-mode.service';
import { analytics } from '@app/events/segment/analytics';

@Component({
  selector: 'app-question-set',
  imports: [
    CommonModule,
    ProgressBarComponent,
    QuestionCardComponent,
  ],
  templateUrl: './question-set.component.html',
  styleUrl: './question-set.component.css'
})
export class QuestionSetComponent implements OnInit, OnDestroy {
  questions: SurveyQuestion[] = [];
  questNo: number = 0;
  totalQuestions: number = 0;
  updatedQuestions: SurveyQuestion[] = [];
  surveyId: string = "";
  userData: SurveyResponse[] = [];
  loadingMessage: string = '';
  private completedSteps: Set<number> = new Set();
  readonly dialog = inject(MatDialog);
  private _snackBar = inject(MatSnackBar);
  private routeSubscription?: Subscription;
  private surveySubscription?: Subscription;
  private questionsSubscription?: Subscription;
  private routerSubscription?: Subscription;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private store: Store<{
      survey: Survey,
      surveyQuestions: SurveyQuestion[],
      userResponse: SurveyResponse[],
    }>,
    private surveyService: SurveysService,
    private spinner: NgxSpinnerService,
    private surveyStore: CareerSurveyStore,
    private surveyStorageService: SurveyStorageService,
    private debugModeService: DebugModeService,
  ) {
    this.surveySubscription = this.store.select('survey').subscribe(value => {
      if (value && value.questions) {
        this.questions = value.questions;
        this.surveyId = value.uuid;
        this.totalQuestions = this.questions.length;
      }
    });
    this.questionsSubscription = this.store.select('surveyQuestions').subscribe(value => {
      if (value) {
        this.updatedQuestions = value;
      }
    });
    this.completedSteps = new Set(this.surveyStorageService.loadCompletedSteps());
  }

  ngOnInit() {
    setTimeout(() => {
      this.updateQuestionFromCurrentUrl();
    }, 0);

    // Listen to route params directly (fires on every in-app survey navigation)
    this.routeSubscription = this.route.params.subscribe(params => {
      if (params['step']) {
        setTimeout(() => {
          this.updateQuestionFromRoute(params);
        }, 0);
      }
    });

    // Listen to ALL router events to catch browser back/forward navigation
    this.routerSubscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      if (event.url.includes('/survey/')) {
        setTimeout(() => {
          this.updateQuestionFromCurrentUrl();
        }, 0);
      }
    });
  }

  private updateQuestionFromCurrentUrl() {
    const currentStep = this.getCurrentStepFromUrl();
    this.updateQuestionFromStep(currentStep);
  }

  ngOnDestroy() {
    this.routeSubscription?.unsubscribe();
    this.surveySubscription?.unsubscribe();
    this.questionsSubscription?.unsubscribe();
    this.routerSubscription?.unsubscribe();
  }

  private updateQuestionFromRoute(params: any) {
    const step = params['step'] ? parseInt(params['step']) : 1;
    this.updateQuestionFromStep(step);
  }

  private getCurrentStepFromUrl(): number {
    const url = this.router.url;
    const match = url.match(/\/survey\/(\d+)/);
    return match ? parseInt(match[1]) : 1;
  }

  private updateQuestionFromStep(step: number) {
    // If the user hasn't clicked "Start Survey" yet, always redirect to the intro.
    if (!this.surveyStorageService.isSurveyStarted()) {
      this.router.navigate(['/survey']);
      return;
    }

    // Convert step to question number (step 1 = question 0)
    const newQuestNo = step - 1;

    let validQuestNo = newQuestNo;
    if (validQuestNo < 0) validQuestNo = 0;
    if (this.totalQuestions > 0 && validQuestNo >= this.totalQuestions) {
      validQuestNo = this.totalQuestions - 1;
    }

    // Guard: redirect to first incomplete step if user skips ahead
    const firstIncomplete = this.getFirstIncompleteStep(validQuestNo);
    if (firstIncomplete !== null) {
      this.router.navigate(['/survey/' + (firstIncomplete + 1)]);
      return;
    }

    setTimeout(() => {
      this.questNo = validQuestNo;
    }, 0);
  }

  private getFirstIncompleteStep(targetQuestNo: number): number | null {
    // Find the lowest question index below targetQuestNo that hasn't been completed
    for (let i = 0; i < targetQuestNo; i++) {
      if (!this.completedSteps.has(i)) {
        return i;
      }
    }
    return null;
  }

  updateQuestion() {
    this.questNo += 1;
    const nextStep = this.questNo + 1; // Convert question number back to step (question 0 = step 1)
    this.router.navigate(['/survey/' + nextStep]);
  }

  onContinueBtnClick() {
    if (!this.questions || this.questions.length === 0) {
      return;
    }

    // Mark this question as completed
    this.completedSteps.add(this.questNo);
    this.surveyStorageService.saveCompletedSteps([...this.completedSteps]);

    const responsePayload = this.writeResponse();

    // Track response submission
    if (responsePayload) {
      analytics.track('Response Submit', {
        section: 'survey',
        item_id: responsePayload.question_id,
        question_number: this.questNo + 1,
        response: responsePayload.response_sequence,
      });
    }

    if (this.questNo < this.questions.length - 1) {
      this.updateQuestion();
    } else {
      // Track survey complete before computing scores
      if (responsePayload) {
        analytics.track('Survey Complete', { section: 'survey' });
      }
      this.getRiasecScore();
    }
  }

  writeResponse() {
    if (!this.questions || !this.questions[this.questNo]) {
      return null;
    }
    const currentQuest = this.questions[this.questNo];
    let userOptions = this.questions[this.questNo].options;
    const index = this.updatedQuestions.findIndex(e => e.id == currentQuest.id);
    if (index >= 0) {
      userOptions = this.updatedQuestions[index].options;
    }

    const updatedData = userOptions.map((item, index) => ({
      question_id: currentQuest.uuid,
      option_id: item.uuid,
      rank: index + 1,
      category: item.riasecCategory,
    }));

    const responseSequence = userOptions.map((opt, idx) => ({
      option_id: opt.uuid,
      text: opt.body,
      rank: idx + 1,
      category: opt.riasecCategory,
    }));

    this.store.dispatch(updateSurveyResponse({ response: updatedData }));

    // Persist accumulated user responses to localStorage to survive page reloads
    this.store.select('userResponse').subscribe(responses => {
      this.surveyStorageService.saveUserResponses(responses);

      // Debug: log running RIASEC score summary
      if (this.debugModeService.isDebugMode && responses && responses.length > 0) {
        const scores: Record<string, number> = {
          realistic: 0,
          investigative: 0,
          artistic: 0,
          social: 0,
          enterprising: 0,
          conventional: 0,
        };
        responses.forEach((r: SurveyResponse) => {
          const points = 6 - r.rank; // rank 1 = 5 pts, rank 6 = 0 pts
          if (r.category in scores) {
            scores[r.category] += points;
          }
        });
        const total = Object.values(scores).reduce((sum, pts) => sum + pts, 0);
        const summary = Object.entries(scores)
          .sort(([, a], [, b]) => b - a)
          .map(([cat, pts]) => `${cat}: ${pts}`)
          .join(' | ');
        console.debug(`[DebugMode] RIASEC Scores → ${summary} | Total: ${total}/90`);
      }
    }).unsubscribe();

    return {
      question_id: currentQuest.uuid,
      response_sequence: responseSequence,
    };
  }

  getRiasecScore() {
    this.spinner.show();
    this.loadingMessage = 'Calculating your results…';
    this.store.select('userResponse').subscribe(value => this.userData = value);

    this.surveyService.saveUserSurveyResponse({
      survey_id: this.surveyId,
      responses: this.userData,
    }).subscribe({
      next: (response) => this.showRiasecScore(response),
      error: (error) => {
        console.log('Error saving user response: ', error);
        this.showErrorMessage('Something went wrong!');
        this.loadingMessage = 'Something went wrong.';
        this.spinner.hide();
      }
    });
  }

  showRiasecScore(data: any) {
    if (data.riasec_score) {
      // Clear persisted survey data after successful submission
      this.surveyStorageService.clearUserResponses();
      this.surveyStorageService.clearSurveyQuestions();
      this.completedSteps.clear();
      this.surveyStorageService.clearCompletedSteps();
      this.surveyStorageService.clearSurveyStarted();
      // Update the cached RIASEC score so subsequent navigations don't re-fetch
      this.surveyService.setCachedRiasecScore(data.riasec_score);
      // Store the RIASEC score in the store
      this.store.dispatch(setRiasecScore({ riasecScore: data.riasec_score }));

      // Also Store the RIASEC score in localStorage in case user navigates to career-matches directly
      try {
        localStorage.setItem('riasec_score', JSON.stringify(data.riasec_score));
      } catch (error) {
        console.warn('Failed to save RIASEC score to localStorage:', error);
      }

      this.dialog.open(ScoresComponent, {
        data: data.riasec_score,
        disableClose: true,
        maxHeight: '90vh',
      });
      // Track survey scores available
      try {
        const score = data.riasec_score || {};
        analytics.track('Survey Scores Available', {
          section: 'survey',
          realistic: score.realistic,
          investigative: score.investigative,
          artistic: score.artistic,
          social: score.social,
          enterprising: score.enterprising,
          conventional: score.conventional,
        });
      } catch (err) {
        console.warn('Failed to track Survey Scores Available', err);
      }
      this.surveyStore.setCareerSurveyTaken();
    } else {
      this.showErrorMessage('Something went wrong!');
    }
    this.spinner.hide();
    this.loadingMessage = 'Results ready.';
  }

  showErrorMessage(msg: string) {
    this._snackBar.openFromComponent(WarningSnackBarComponent, {
      ...WarningDefaultOptions,
      data: {
        message: msg,
      },
    });
  }
}
